import React from 'react';
import vars from '@variables';
import styled from '@emotion/styled/macro';
import { ContentfulIconAtom } from '../icon-atoms/icon-atoms';
import { getGtmLinkId } from '@utils/utils';
import { GTM_ID } from '../../../../common/constants/gtmIds';
import { H3Atom } from '../text-atoms/text-atoms';
import { Link } from 'gatsby';
import { FlexibleLinkProps, PageTypeLinkProps } from './link-atoms.types';

export const AAtom = styled.a`
  color: ${vars.coreDarkGrey};
`;

export const NoUnderlineLinkAtom = styled.a`
  text-decoration: none;
`;

const FlexibleLink: React.FC<FlexibleLinkProps> = ({
  url,
  children,
  onClickFunction,
  isInline = false,
  gtmId,
  className,
  dataTestId,
  openInNewTab,
}) => {
  if (!children && isInline) {
    return null;
  }

  if (url && typeof url === 'string') {
    const shouldOpenInNewTab =
      openInNewTab !== undefined
        ? openInNewTab
        : url.startsWith('http') || url.startsWith('mailto');

    if (
      /^https?:\/\/(www\.)?(ellenmacarthurfoundation\.org|emf\.org)\/?/.test(
        url
      )
    ) {
      return (
        <Link
          to={url.replace(
            /https?:\/\/(www\.)?(ellenmacarthurfoundation\.org|emf\.org)\/?/,
            '/'
          )}
          className={className}
          data-testid={dataTestId || 'internal-link'}
          data-gtm-id={gtmId || GTM_ID.INTERNAL_LINK}
          onClick={onClickFunction}
          target={shouldOpenInNewTab ? '_blank' : undefined}
          rel={shouldOpenInNewTab ? 'noreferrer noopener' : undefined}
        >
          {children || url}
        </Link>
      );
    } else if (url.startsWith('http')) {
      return (
        <a
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          className={className}
          data-testid={dataTestId || 'external-link'}
          data-gtm-id={gtmId ? gtmId : url ? getGtmLinkId(url) : null}
        >
          {children || url}
        </a>
      );
    } else if (url.startsWith('mailto')) {
      return (
        <a
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          className={className}
          data-testid={dataTestId || 'mailto-link'}
          data-gtm-id={gtmId || GTM_ID.MAILTO_LINK}
        >
          {children || url}
        </a>
      );
    } else {
      return (
        <Link
          to={url}
          className={className}
          data-testid={dataTestId || 'internal-link'}
          data-gtm-id={gtmId || GTM_ID.INTERNAL_LINK}
          onClick={onClickFunction}
          target={shouldOpenInNewTab ? '_blank' : undefined}
          rel={shouldOpenInNewTab ? 'noreferrer noopener' : undefined}
        >
          {children || url}
        </Link>
      );
    }
  } else if (isInline) {
    return (
      <span className={className} data-testid={'non-link-inline'}>
        {children}
      </span>
    );
  }

  return (
    <div className={className} data-testid={'non-link-block'}>
      {children}
    </div>
  );
};

export const FlexibleLinkAtom = styled(FlexibleLink)``;

export const FlexibleLinkAtomForLanguageSwitcher = styled(FlexibleLink)`
  color: white;
  margin: 0 2.4rem 0 0;
  font-size: 1.4rem;
  font-weight: ${vars.fontWeightRegular};
  text-decoration: none;
  line-height: 1.6;
  :first-letter {
    text-transform: uppercase;
  }
`;

export const LetterboxLinkAtom = styled(FlexibleLink)`
  display: block;
  min-width: 19.6rem;
  ${vars.fontSize2}
  font-weight: ${vars.fontWeightBold};
  text-decoration: none;
  text-align: center;
  &:not(:last-of-type) {
    margin-bottom: ${vars.spacingM};
  }
  padding: 1.4rem 0;
  color: ${vars.coreDarkGrey};
  background: white;
  border: 1px solid ${vars.coreDarkGrey};
  border-radius: 2px;
  :first-letter {
    text-transform: uppercase;
  }
  &:hover {
    background: ${vars.coreDarkGrey};
    color: white;
    cursor: pointer;
    transition: ${vars.defaultTransition};
  }
`;

export const WhiteLetterboxLinkAtom = styled(FlexibleLink)`
  margin: 0 auto;
  min-width: 16rem;
  padding: 1.4rem 0;
  border: 1px solid ${vars.transparentBorderGrey};
  border-radius: 2px;
  color: ${vars.coreDarkGrey};
  background: ${vars.white};
  ${vars.fontSize2}
  font-weight: ${vars.fontWeightBold};
  text-align: center;
  text-decoration: none;
  &:first-letter {
    text-transform: uppercase;
  }
`;

export const InternalLinkAtom = styled(FlexibleLink)`
  color: ${vars.coreDarkGrey};
  :first-letter {
    text-transform: uppercase;
  }
`;

const PageTypeLink: React.FC<PageTypeLinkProps> = ({
  pageType,
  url,
  className,
}) => {
  if (!pageType) return null;

  return (
    <H3Atom className={className} data-testid="page-type-link">
      {url ? (
        <InternalLinkAtom url={url}>{pageType}</InternalLinkAtom>
      ) : (
        <span className="no-link">{pageType}</span>
      )}
    </H3Atom>
  );
};

export const StyledPageTypeLink = styled(PageTypeLink)`
  padding: 0;
  ${InternalLinkAtom}, .no-link {
    font-weight: ${vars.fontWeightBold};
    color: ${({ isLight }) => (isLight ? vars.white : vars.coreDarkGrey)};
    text-decoration: none;
    text-transform: capitalize;
    padding-bottom: ${vars.spacingXs};
    border-color: ${({ isLight }) =>
      isLight ? vars.white : vars.coreDarkGrey};
    border-bottom: ${vars.spacingXs} solid;
    ${vars.fontSize3}
    @media screen and (min-width: ${vars.breakPointTablet}) {
      padding-bottom: ${vars.spacingS};
      ${vars.fontSize6}
      border-bottom: ${vars.spacingS} solid;
    }
  }
`;

const ContentfulLink = ({ url, text, className = '' }) => (
  <FlexibleLink url={url} className={className}>
    <ContentfulIconAtom />
    {text}
  </FlexibleLink>
);

export const ContentfulLinkAtom = styled(ContentfulLink)`
  position: relative;
  padding-left: 18px;

  svg {
    position: absolute;
    top: -3px;
    left: -5px;
  }
`;

export const TabLinkAtom = styled(FlexibleLink)`
  position: relative;
  display: block;
  ${vars.fontSize2};
  font-weight: ${vars.fontWeightMedium};
  color: ${vars.mordorGrey};
  letter-spacing: -0.01em;
  text-decoration: none;
  white-space: nowrap;
  margin-top: 4px;
  -webkit-font-smoothing: subpixel-antialiased;

  &:first-letter {
    text-transform: capitalize;
  }

  &:not(:last-of-type) {
    margin-right: ${vars.spacingL};
  }

  &::after {
    content: '';
    border-bottom: 3px solid ${vars.coreDarkGrey};
    position: absolute;
    bottom: -8px;
    left: 0;
    right: 0;
    z-index: 999;
    border-radius: 3px;
    display: block;
    transform: scaleX(0);
    transform-origin: top center;
    transition: transform 0.3s ease;
  }

  &.active {
    &::after {
      transform: scaleX(1);
    }
  }

  &:hover {
    &::after {
      transform: scaleX(1);
    }
    cursor: pointer;
  }

  @media screen and (min-width: ${vars.breakPointTablet}) {
    &.active::after,
    &:hover::after {
      bottom: -8px;
    }
  }
`;
